import { useEffect, useMemo, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import "../styles/Dashboard.css";
import { deleteData, getData, postData, putData } from "../services/Request";
import { toast } from "react-toastify";
import { useLoader } from "../context/LoaderContext";
import Select from "react-select";
import { saleTypeOptions, saleTaxOptions } from "../constants/saleTypes";
import { SRO_ITEMS } from "../constants/scheduleNo";

const emptyForm = {
  name: "",
  hscode: "",
  description: "",
  uom: "",
  price: "",
  sale_tax: "",
  withholding_tax: "",
  extra_tax: "",
  further_tax: "",
  sale_type: "",
  sroScheduleNo: "",
  fedPayable: "",
  discount: "",
  sroItemSerialNo: "",
};

const numericFields = [
  "price",
  "sale_tax",
  "withholding_tax",
  "extra_tax",
  "further_tax",
  "fedPayable",
  "discount",
];

const getProductId = (product) => product?.id ?? product?._id ?? "";
const hasValue = (value) => `${value ?? ""}`.trim().length > 0;

const sroScheduleOptions = Object.keys(SRO_ITEMS).map((key) => ({
  value: key,
  label: key,
}));

const uomOptions = [
  { value: "KG", label: "KG" },
  { value: "LTR", label: "LTR" },
  { value: "PCS", label: "PCS" },
  { value: "MTR", label: "MTR" },
];

const normaliseList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.products)) return payload.products;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const sanitizeDecimal = (value) => {
  const clean = String(value).replace(/[^0-9.]/g, "");
  if (!clean.includes(".")) return clean;
  const [whole, ...rest] = clean.split(".");
  return `${whole}.${rest.join("").slice(0, 2)}`;
};

export default function Products() {
  const { showLoader, hideLoader } = useLoader();
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const hasFetchedInitial = useRef(false);
  const requiredFields = [
    "name",
    "hscode",
    "description",
    "price",
    "sale_tax",
    "sale_type",
  ];
  const isFormComplete = requiredFields.every((field) =>
    hasValue(formData[field]),
  );

  const fetchProducts = async ({ withLoader = false } = {}) => {
    try {
      if (withLoader) {
        showLoader();
      } else {
        setListLoading(true);
      }
      const response = await getData(
        `api/products?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(
          search,
        )}`,
      );
      setProducts(normaliseList(response));
      const pagination =
        response?.pagination || response?.data?.pagination || {};
      setTotalPages(pagination?.totalPages || 1);
    } catch (error) {
      toast.error("Products is not fetch.");
      console.error("Failed to fetch products", error);
    } finally {
      if (withLoader) {
        hideLoader();
      } else {
        setListLoading(false);
      }
    }
  };

  useEffect(() => {
    if (hasFetchedInitial.current) return;
    hasFetchedInitial.current = true;
    fetchProducts({ withLoader: true });
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [page, search]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const nextValue = numericFields.includes(name)
      ? sanitizeDecimal(value)
      : value;
    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    const payload = { ...formData };
    numericFields.forEach((field) => {
      payload[field] =
        formData[field] === "" || formData[field] === null
          ? 0
          : parseFloat(formData[field]);
    });
    try {
      if (editingId) {
        await putData(`api/products/${editingId}`, payload);
        toast.success("Product is updated");
      } else {
        await postData("api/products", payload);
        toast.success("Product is added");
      }
      resetForm();
      await fetchProducts();
    } catch (error) {
      const action = editingId ? "update" : "add";
      toast.error(`Product not ${action}.`);
      console.error("Submit product failed", error);
    } finally {
      setSubmitting(false);
    }
  };

  const loadProductForEdit = async (productId) => {
    if (!productId) {
      toast.error("Product ID not found.");
      return;
    }
    try {
      showLoader();
      const response = await getData(`api/products/${productId}`);
      const payload =
        response?.data && !Array.isArray(response.data)
          ? response.data
          : response;
      setFormData({
        ...emptyForm,
        ...payload,
      });
      setEditingId(productId);
    } catch (error) {
      toast.error("Product details cannot loaded.");
      console.error("Failed to fetch single product", error);
    } finally {
      hideLoader();
    }
  };

  const handleDelete = async (productId) => {
    const targetId = productId || confirmDeleteId;
    if (!targetId) {
      toast.error("Product ID not found.");
      return;
    }
    setDeletingId(targetId);
    const toastId = toast.loading("Deleting product...");
    try {
      showLoader();
      await deleteData(`api/products/${targetId}`);
      toast.update(toastId, {
        render: "Product deleted.",
        type: "success",
        isLoading: false,
        autoClose: 1500,
      });
      if (editingId === targetId) {
        resetForm();
      }
      await fetchProducts();
    } catch (error) {
      toast.update(toastId, {
        render: "Product could not be deleted. Please try again.",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
      console.error("Failed to delete product", error);
    } finally {
      hideLoader();
      setConfirmDeleteId(null);
      setDeletingId(null);
    }
  };

  const openDeleteModal = (productId) => setConfirmDeleteId(productId);
  const closeDeleteModal = () => setConfirmDeleteId(null);

  const totalProducts = useMemo(() => products.length, [products]);
  const sroItemOptions = useMemo(() => {
    const serials = SRO_ITEMS[formData.sroScheduleNo] || [];
    return serials.map((serial) => ({ value: serial, label: serial }));
  }, [formData.sroScheduleNo]);

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="app-content">
        <Navbar />
        <div className="dashboard-page">
          <main className="dashboard">
            <section className="sample-page product-page">
              <div className="sample-page__header">
                <p className="eyebrow">Product Management</p>
                <h1>Products</h1>
                <p></p>
              </div>

              <div className="sample-page__stats">
                <div className="sample-stat">
                  <p>Total products</p>
                  <h3>{totalProducts}</h3>
                </div>
              </div>

              <div className="product-management">
                <div className="seller-card seller-form-card">
                  <div className="seller-card__header">
                    <h3>{editingId ? "Update Product" : "Add Product"}</h3>
                    {editingId && (
                      <button
                        type="button"
                        className="text-link-btn"
                        onClick={resetForm}
                      >
                        Cancel edit
                      </button>
                    )}
                  </div>
                  <form className="seller-form" onSubmit={handleSubmit}>
                    <div className="form-grid">
                      <label className="form-field">
                        <span>
                          Name <span className="requied">*</span>
                        </span>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          maxLength="120"
                          placeholder="Product name"
                        />
                      </label>

                      <label className="form-field">
                        <span>
                          HS Code <span className="requied">*</span>
                        </span>
                        <input
                          type="text"
                          name="hscode"
                          value={formData.hscode}
                          onChange={handleInputChange}
                          required
                          maxLength="20"
                          placeholder="HS code"
                        />
                      </label>

                      <label className="form-field">
                        <span>
                          Description <span className="requied">*</span>
                        </span>
                        <input
                          type="text"
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          required
                          maxLength="255"
                          placeholder="Product description"
                        />
                      </label>

                      <label className="form-field">
                        <span>UOM</span>
                        <Select
                          options={uomOptions}
                          placeholder="Select UOM"
                          isSearchable={true}
                          value={
                            uomOptions.find(
                              (opt) => opt.value === formData.uom,
                            ) || null
                          }
                          onChange={(selectedOption) =>
                            setFormData((prev) => ({
                              ...prev,
                              uom: selectedOption?.value || "",
                            }))
                          }
                          isClearable
                        />
                      </label>

                      <label className="form-field">
                        <span>
                          Price <span className="requied">*</span>
                        </span>
                        <input
                          type="text"
                          name="price"
                          value={formData.price}
                          onChange={handleInputChange}
                          required
                          inputMode="decimal"
                          placeholder="0.00"
                        />
                      </label>

                      <label className="form-field">
                        <span>
                          Sale Tax <span className="requied">*</span>
                        </span>

                        <Select
                          options={saleTaxOptions}
                          placeholder="Select tax %"
                          isSearchable={true}
                          value={
                            saleTaxOptions.find(
                              (opt) => opt.value === formData.sale_tax,
                            ) || null
                          }
                          onChange={(selectedOption) =>
                            setFormData((prev) => ({
                              ...prev,
                              sale_tax: selectedOption?.value || "",
                            }))
                          }
                          isClearable
                        />
                      </label>

                      <label className="form-field">
                        <span>Withholding tax</span>
                        <input
                          type="text"
                          name="withholding_tax"
                          value={formData.withholding_tax}
                          onChange={handleInputChange}
                          inputMode="decimal"
                          placeholder="0.00"
                        />
                      </label>

                      <label className="form-field">
                        <span>Extra Tax</span>
                        <input
                          type="text"
                          name="extra_tax"
                          value={formData.extra_tax}
                          onChange={handleInputChange}
                          inputMode="decimal"
                          placeholder="0.00"
                        />
                      </label>

                      <label className="form-field">
                        <span>Further Tax</span>
                        <input
                          type="text"
                          name="further_tax"
                          value={formData.further_tax}
                          onChange={handleInputChange}
                          inputMode="decimal"
                          placeholder="0.00"
                        />
                      </label>

                      {/* <label className="form-field">
                        <span>
                          Sale Type<span className="requied">*</span>
                        </span>
                        <input
                          type="text"
                          name="sale_type"
                          value={formData.sale_type}
                          onChange={handleInputChange}
                          maxLength="50"
                          placeholder="Retail / wholesale"
                          required
                        />
                      </label> */}
                      <label className="form-field">
                        <span>
                          Sale Type<span className="requied">*</span>
                        </span>

                        <Select
                          options={saleTypeOptions}
                          placeholder="Select Sale Type"
                          isSearchable={true}
                          value={
                            saleTypeOptions.find(
                              (opt) => opt.value === formData.sale_type,
                            ) || null
                          }
                          onChange={(selectedOption) =>
                            setFormData((prev) => ({
                              ...prev,
                              sale_type: selectedOption?.value || "",
                            }))
                          }
                          isClearable
                        />
                      </label>

                      <label className="form-field">
                        <span>SRO Schedule No</span>
                        <Select
                          options={sroScheduleOptions}
                          placeholder="Select SRO Schedule"
                          isSearchable={true}
                          value={
                            sroScheduleOptions.find(
                              (opt) => opt.value === formData.sroScheduleNo,
                            ) || null
                          }
                          onChange={(selectedOption) => {
                            const nextSchedule = selectedOption?.value || "";
                            const nextSerial =
                              (SRO_ITEMS[nextSchedule] &&
                                SRO_ITEMS[nextSchedule][0]) ||
                              "";
                            setFormData((prev) => ({
                              ...prev,
                              sroScheduleNo: nextSchedule,
                              sroItemSerialNo: nextSerial,
                            }));
                          }}
                          isClearable
                        />
                      </label>
                      <label className="form-field">
                        <span>SRO Item Serial No</span>
                        <Select
                          options={sroItemOptions}
                          placeholder="Select Serial"
                          isSearchable={true}
                          value={
                            sroItemOptions.find(
                              (opt) => opt.value === formData.sroItemSerialNo,
                            ) || null
                          }
                          onChange={(selectedOption) =>
                            setFormData((prev) => ({
                              ...prev,
                              sroItemSerialNo: selectedOption?.value || "",
                            }))
                          }
                          isClearable
                          isDisabled={!formData.sroScheduleNo}
                        />
                      </label>
                      <label className="form-field">
                        <span>FED Payable</span>
                        <input
                          type="text"
                          name="fedPayable"
                          value={formData.fedPayable}
                          onChange={handleInputChange}
                          inputMode="decimal"
                          placeholder="0.00"
                        />
                      </label>

                      <label className="form-field">
                        <span>Discount</span>
                        <input
                          type="text"
                          name="discount"
                          value={formData.discount}
                          onChange={handleInputChange}
                          inputMode="decimal"
                          placeholder="0.00"
                        />
                      </label>
                    </div>

                    <button
                      type="submit"
                      className="primary-btn"
                      disabled={submitting || !isFormComplete}
                    >
                      {submitting
                        ? "Please wait..."
                        : editingId
                          ? "Update Product"
                          : "Add Product"}
                    </button>
                  </form>
                </div>

                <div className="seller-card seller-table-card">
                  <div className="seller-card__header">
                    <h3>Product list</h3>
                    <button
                      type="button"
                      className="text-link-btn"
                      onClick={() => fetchProducts()}
                      disabled={listLoading}
                    >
                      {listLoading ? "Refreshing..." : "Refresh"}
                    </button>
                  </div>
                  <div
                    className="list-actions"
                    style={{ marginBottom: "10px" }}
                  >
                    <input
                      type="search"
                      placeholder="Search products"
                      value={search}
                      onChange={(e) => {
                        setPage(1);
                        setSearch(e.target.value);
                      }}
                    />
                  </div>
                  <div className="seller-table-wrapper">
                    <table className="seller-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Name</th>
                          <th>HS Code</th>
                          <th>Price</th>
                          <th>Sale tax</th>
                          <th>Withholding</th>
                          <th>Sale type</th>

                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.length === 0 && (
                          <tr>
                            <td colSpan="13" className="empty-state">
                              {listLoading
                                ? "Loading products..."
                                : "No Products Found."}
                            </td>
                          </tr>
                        )}
                        {products.map((product, index) => {
                          const productId = getProductId(product);
                          const rowKey =
                            productId ?? `${product?.hscode ?? "row"}-${index}`;
                          return (
                            <tr key={rowKey}>
                              <th>{index + 1}</th>
                              <td>{product?.name || "-"}</td>
                              <td>{product?.hscode || "-"}</td>
                              <td>{product?.price ?? "-"}</td>
                              <td>{product?.sale_tax ?? "-"}</td>
                              <td>{product?.withholding_tax ?? "-"}</td>
                              <td>{product?.sale_type || "-"}</td>

                              <td>
                                <div className="table-actions">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      loadProductForEdit(productId)
                                    }
                                    disabled={!productId}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="danger"
                                    onClick={() => openDeleteModal(productId)}
                                    disabled={!productId || deletingId !== null}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-pagination">
                    <button
                      type="button"
                      disabled={page <= 1 || listLoading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Prev
                    </button>
                    <span>
                      Page {page} of {totalPages}
                    </span>
                    <button
                      type="button"
                      disabled={page >= totalPages || listLoading}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                  {confirmDeleteId && (
                    <div className="modal-backdrop">
                      <div className="modal-card">
                        <h3>Delete Product</h3>
                        <p>
                          This action cannot be undone. Delete this product?
                        </p>
                        <div className="modal-actions">
                          <button
                            type="button"
                            className="outline-btn"
                            onClick={closeDeleteModal}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="primary-btn danger"
                            onClick={() => handleDelete(confirmDeleteId)}
                            disabled={deletingId !== null}
                          >
                            {deletingId === confirmDeleteId ? (
                              <>
                                <span
                                  className="btn-spinner"
                                  aria-hidden="true"
                                />
                                Deleting...
                              </>
                            ) : (
                              "Delete"
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}
